import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Card,
  Button,
  Typography,
  Space,
  Popconfirm,
  Modal,
  Form,
  Input,
  App,
} from 'antd';
import {
  MenuOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HolderOutlined,
  LinkOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { MenuItem } from '../types/website';

const { Title, Text } = Typography;

interface DndKitMenuDragDropProps {
  menuItems: MenuItem[];
  onChange: (menuItems: MenuItem[]) => void;
  onShuffle?: () => void;
}

interface SortableMenuItemProps {
  item: MenuItem;
  onEdit: (item: MenuItem) => void;
  onDelete: (id: string) => void;
  onAddSubmenu: (parentId: string) => void;
  onMoveToSubmenu: (itemId: string, parentId: string) => void;
  isSubmenu?: boolean;
  isDragOver?: boolean;
  canDropSubmenu?: boolean;
  previewMode?: 'none' | 'submenu' | 'main';
  activeItem?: MenuItem | null;
  previewLocation?: 'main' | 'submenu' | 'none';
}

const SortableMenuItem: React.FC<SortableMenuItemProps> = React.memo(({
  item,
  onEdit,
  onDelete,
  onAddSubmenu,
  onMoveToSubmenu,
  isSubmenu = false,
  isDragOver = false,
  canDropSubmenu = false,
  previewMode = 'none',
  activeItem = null,
  previewLocation = 'none'
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
            <Card
              size="small"
              style={{
                backgroundColor: isDragging ? 'var(--bg-hover)' : (isDragOver && canDropSubmenu ? 'var(--bg-tertiary)' : 'var(--bg-card)'),
                borderTop: isDragging ? '2px dashed var(--info)' : (isDragOver && canDropSubmenu ? '2px solid var(--success)' : '1px solid var(--border-secondary)'),
                borderRight: isDragging ? '2px dashed var(--info)' : (isDragOver && canDropSubmenu ? '2px solid var(--success)' : '1px solid var(--border-secondary)'),
                borderBottom: isDragging ? '2px dashed var(--info)' : (isDragOver && canDropSubmenu ? '2px solid var(--success)' : '1px solid var(--border-secondary)'),
                borderLeft: isSubmenu ? '3px solid var(--info)' : (isDragging ? '2px dashed var(--info)' : (isDragOver && canDropSubmenu ? '2px solid var(--success)' : '1px solid var(--border-secondary)')),
                boxShadow: isDragging ? 'var(--shadow-medium)' : (isDragOver && canDropSubmenu ? 'var(--shadow-light)' : 'none'),
                marginBottom: '8px',
                marginLeft: isSubmenu ? '20px' : '0px',
                transition: 'all 0.2s ease'
              }}
              styles={{ body: { padding: '12px' } }}
            >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            {...listeners}
            style={{
              cursor: 'grab',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: 'var(--bg-hover)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '32px',
              height: '32px',
              border: '2px solid var(--border-primary)'
            }}
          >
            <HolderOutlined style={{ color: 'var(--text-secondary)', fontSize: '16px' }} />
          </div>
          
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Text strong>{item.label}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                <LinkOutlined /> {item.url}
              </Text>
              {isSubmenu && (
                <Text type="secondary" style={{ fontSize: '10px', color: '#1890ff' }}>
                  (Submenu)
                </Text>
              )}
            </div>
            {item.children && item.children.length > 0 && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {item.children.length} submenu{item.children.length > 1 ? 's' : ''}
              </Text>
            )}
          </div>

          <Space size="small">
            {!isSubmenu && (
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => onAddSubmenu(item.id)}
              />
            )}
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(item)}
            />
            <Popconfirm
              title="Delete menu item?"
              description="This action cannot be undone."
              onConfirm={() => onDelete(item.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Space>
        </div>

        {/* Render submenu items */}
        {item.children && item.children.length > 0 && (
          <div style={{ marginTop: '8px', paddingLeft: '20px' }}>
            <SortableContext items={item.children.map(child => child.id)} strategy={verticalListSortingStrategy}>
              {item.children.map((child) => (
                <SortableMenuItem
                  key={child.id}
                  item={child}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onAddSubmenu={onAddSubmenu}
                  onMoveToSubmenu={onMoveToSubmenu}
                  isSubmenu={true}
                  isDragOver={false}
                  canDropSubmenu={false}
                  previewMode="none"
                  activeItem={null}
                  previewLocation="none"
                />
              ))}
            </SortableContext>
          </div>
        )}

        {/* Preview: Show what the item will become when dropped - Main to Submenu - Only show if preview location is submenu */}
        {isDragOver && previewMode === 'submenu' && activeItem && !isSubmenu && activeItem.id !== item.id && previewLocation === 'submenu' && (
          <div style={{ 
            marginTop: '8px', 
            paddingLeft: '20px',
            opacity: 0.6,
            transform: 'scale(0.9)',
            transition: 'opacity 0.15s ease, transform 0.15s ease'
          }}>
            <Card
              size="small"
              style={{ 
                backgroundColor: 'var(--bg-tertiary)',
                border: '2px dashed var(--info)',
                boxShadow: 'var(--shadow-light)',
                marginBottom: '8px'
              }}
              styles={{ body: { padding: '8px' } }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  padding: '6px',
                  borderRadius: '3px',
                  backgroundColor: 'var(--bg-hover)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '24px',
                  height: '24px',
                  border: '1px solid var(--info)'
                }}>
                  <HolderOutlined style={{ color: 'var(--info)', fontSize: '12px' }} />
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <Text strong style={{ color: 'var(--info)', fontSize: '12px' }}>{activeItem.label}</Text>
                    <Text type="secondary" style={{ fontSize: '10px' }}>
                      (Will become submenu)
                    </Text>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Preview: Show what submenu will become when moved to main - Only show if preview location is main */}
        {isDragOver && previewMode === 'main' && activeItem && isSubmenu && activeItem.id !== item.id && previewLocation === 'main' && (
          <div style={{ 
            marginTop: '8px',
            opacity: 0.6,
            transform: 'scale(0.9)',
            transition: 'opacity 0.15s ease, transform 0.15s ease'
          }}>
            <Card
              size="small"
              style={{ 
                backgroundColor: 'var(--bg-tertiary)',
                border: '2px dashed var(--success)',
                boxShadow: 'var(--shadow-light)',
                marginBottom: '8px'
              }}
              styles={{ body: { padding: '8px' } }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  padding: '6px',
                  borderRadius: '3px',
                  backgroundColor: 'var(--bg-hover)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '24px',
                  height: '24px',
                  border: '1px solid var(--success)'
                }}>
                  <HolderOutlined style={{ color: 'var(--success)', fontSize: '12px' }} />
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <Text strong style={{ color: 'var(--success)', fontSize: '12px' }}>{activeItem.label}</Text>
                    <Text type="secondary" style={{ fontSize: '10px' }}>
                      (Will become main menu)
                    </Text>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </Card>
    </div>
  );
});

SortableMenuItem.displayName = 'SortableMenuItem';

const DndKitMenuDragDrop: React.FC<DndKitMenuDragDropProps> = React.memo(({ menuItems, onChange, onShuffle }) => {
  const { message } = App.useApp();
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'none' | 'submenu' | 'main'>('none');
  const [previewLocation, setPreviewLocation] = useState<'main' | 'submenu' | 'none'>('none');
  
  // Debounce timer ref
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Cleanup effect
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Helper function to find item by ID in nested structure
  const findItemById = React.useCallback((items: MenuItem[], id: string): MenuItem | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Helper function to remove item by ID from nested structure
  const removeItemById = React.useCallback((items: MenuItem[], id: string): MenuItem[] => {
    return items.filter(item => {
      if (item.id === id) return false;
      if (item.children) {
        item.children = removeItemById(item.children, id);
      }
      return true;
    });
  }, []);

  // Helper function to add item to parent's children
  const addItemToParent = React.useCallback((items: MenuItem[], parentId: string, newItem: MenuItem): MenuItem[] => {
    return items.map(item => {
      if (item.id === parentId) {
        return {
          ...item,
          children: [...(item.children || []), newItem]
        };
      }
      if (item.children) {
        return {
          ...item,
          children: addItemToParent(item.children, parentId, newItem)
        };
      }
      return item;
    });
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = React.useCallback((event: DragOverEvent) => {
    const { over, active } = event;
    const newOverId = over ? over.id as string : null;
    
    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Use requestAnimationFrame for smoother performance
    debounceTimerRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        // Only update state if values actually changed to prevent unnecessary re-renders
        setOverId(prev => prev !== newOverId ? newOverId : prev);
        
        if (newOverId && active.id) {
          const activeId = active.id as string;
          
          // Check if active item is a submenu (not in main menuItems array)
          const isActiveItemSubmenu = !menuItems.some(item => item.id === activeId);
          
          // Check if over target is a main menu item
          const isOverMainMenuItem = menuItems.some(item => item.id === newOverId);
          
          let newPreviewMode: 'none' | 'submenu' | 'main' = 'none';
          let newPreviewLocation: 'main' | 'submenu' | 'none' = 'none';
          
          if (isActiveItemSubmenu && !isOverMainMenuItem) {
            // Dragging submenu item to empty space or outside - will become main menu
            newPreviewMode = 'main';
            newPreviewLocation = 'main';
          } else if (!isActiveItemSubmenu && isOverMainMenuItem && activeId !== newOverId) {
            // Dragging main menu item to another main menu - will become submenu
            newPreviewMode = 'submenu';
            newPreviewLocation = 'submenu';
          }
          
          setPreviewMode(prev => prev !== newPreviewMode ? newPreviewMode : prev);
          setPreviewLocation(prev => prev !== newPreviewLocation ? newPreviewLocation : prev);
        } else {
          setPreviewMode(prev => prev !== 'none' ? 'none' : prev);
          // Don't reset previewLocation when no overId - keep current location
        }
      });
    }, 8); // Faster response
  }, [menuItems]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    setActiveId(null);
    setOverId(null);
    setPreviewMode('none');
    setPreviewLocation('none');

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Find the active item
    const activeItem = findItemById(menuItems, activeId);
    if (!activeItem) return;

    // Check if active item is currently a submenu item
    const isActiveItemSubmenu = !menuItems.some(item => item.id === activeId);

    // If dropping on a main menu item
    if (menuItems.some(item => item.id === overId)) {
      const overIndex = menuItems.findIndex(item => item.id === overId);
      
      if (isActiveItemSubmenu) {
        // Moving from submenu to main menu
        const itemsWithoutActive = removeItemById(menuItems, activeId);
        const newItems = [...itemsWithoutActive];
        newItems.splice(overIndex, 0, activeItem);
        onChange(newItems);
        message.success('Moved to main menu!');
      } else {
        // Check if we're trying to move a main menu item to become a submenu of another main menu item
        if (activeId !== overId) {
          // Remove the active item from main menu
          const itemsWithoutActive = menuItems.filter(item => item.id !== activeId);
          // Add it as a submenu to the target main menu item
          const newItems = itemsWithoutActive.map(item => {
            if (item.id === overId) {
              return {
                ...item,
                children: [...(item.children || []), activeItem]
              };
            }
            return item;
          });
          onChange(newItems);
          message.success('Moved to submenu!');
        }
      }
    }
    // If dropping on a submenu item, move to that parent
    else {
      // Find the parent of the over item
      let parentId: string | null = null;
      for (const item of menuItems) {
        if (item.children && item.children.some(child => child.id === overId)) {
          parentId = item.id;
          break;
        }
      }

      if (parentId) {
        // Remove from current location
        const itemsWithoutActive = removeItemById(menuItems, activeId);
        // Add to new parent
        const newItems = addItemToParent(itemsWithoutActive, parentId, activeItem);
        onChange(newItems);
        message.success('Moved to submenu!');
      }
    }
  };

  const generateId = () => {
    return `menu-item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const handleAddNew = () => {
    const newItem: MenuItem = {
      id: generateId(),
      label: 'New Menu Item',
      url: '#new-item',
    };
    
    // Auto add to menu immediately
    const newItems = [...menuItems, newItem];
    onChange(newItems);
    message.success('Menu item added! You can edit it by clicking the edit button.');
  };

  const handleEdit = React.useCallback((item: MenuItem) => {
    setEditingItem(item);
    setIsAddingNew(false);
  }, []);

  const handleSaveEdit = React.useCallback((values: { label: string; url: string }) => {
    if (editingItem) {
      const updatedItem = { ...editingItem, ...values };
      
      if (isAddingNew) {
        const newItems = [...menuItems, updatedItem];
        onChange(newItems);
        message.success('Menu item added successfully');
      } else {
        // Helper function to update item in nested structure
        const updateItemInMenu = (items: MenuItem[]): MenuItem[] => {
          return items.map(item => {
            if (item.id === editingItem.id) {
              return updatedItem;
            }
            if (item.children) {
              return {
                ...item,
                children: updateItemInMenu(item.children)
              };
            }
            return item;
          });
        };
        
        const newItems = updateItemInMenu(menuItems);
        onChange(newItems);
        message.success('Menu item updated successfully');
      }
      
      setEditingItem(null);
      setIsAddingNew(false);
    }
  }, [editingItem, isAddingNew, menuItems, onChange, message]);

  const handleDelete = React.useCallback((id: string) => {
    const newItems = removeItemById(menuItems, id);
    onChange(newItems);
    message.success('Menu item deleted successfully');
  }, [menuItems, onChange, removeItemById, message]);

  const handleAddSubmenu = React.useCallback((parentId: string) => {
    const newSubmenuItem: MenuItem = {
      id: generateId(),
      label: 'New Submenu Item',
      url: '#submenu-item',
    };
    
    const newItems = addItemToParent(menuItems, parentId, newSubmenuItem);
    onChange(newItems);
    message.success('Submenu item added!');
  }, [menuItems, onChange, addItemToParent, message]);

  const handleMoveToSubmenu = React.useCallback((itemId: string, parentId: string) => {
    const item = findItemById(menuItems, itemId);
    if (!item) return;

    const itemsWithoutItem = removeItemById(menuItems, itemId);
    const newItems = addItemToParent(itemsWithoutItem, parentId, item);
    onChange(newItems);
    message.success('Moved to submenu!');
  }, [menuItems, onChange, findItemById, removeItemById, addItemToParent, message]);


  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>
          <MenuOutlined /> Menu Structure
        </Title>
        <Space>
          {onShuffle && (
            <Popconfirm
              title="Shuffle Menu Items"
              description="Are you sure you want to shuffle the menu items? This will randomize their order."
              onConfirm={onShuffle}
              okText="Yes, Shuffle"
              cancelText="Cancel"
              okButtonProps={{ type: 'primary' }}
            >
              <Button 
                icon={<ReloadOutlined />}
                disabled={menuItems.length === 0}
              >
                Shuffle the list
              </Button>
            </Popconfirm>
          )}
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddNew}
          >
            Add Menu Item
          </Button>
        </Space>
      </div>

      {menuItems.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Text type="secondary">No menu items yet. Click "Add Menu Item" to add a new item instantly.</Text>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={menuItems.map(item => item.id)} strategy={verticalListSortingStrategy}>
            <div style={{ minHeight: '100px' }}>
              {menuItems.map((item) => (
                <SortableMenuItem
                  key={item.id}
                  item={item}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onAddSubmenu={handleAddSubmenu}
                  onMoveToSubmenu={handleMoveToSubmenu}
                  isSubmenu={false}
                  isDragOver={overId === item.id}
                  canDropSubmenu={!!(activeId && (
                    // Can drop submenu item to main menu
                    !menuItems.some(mainItem => mainItem.id === activeId) ||
                    // Can drop main menu item to become submenu of another main menu item
                    (menuItems.some(mainItem => mainItem.id === activeId) && activeId !== item.id)
                  ))}
                  previewMode={overId === item.id ? previewMode : 'none'}
                  activeItem={overId === item.id ? findItemById(menuItems, activeId || '') : null}
                  previewLocation={previewLocation}
                />
              ))}
            </div>
          </SortableContext>
          
          <DragOverlay>
            {activeId ? (
              <Card
                size="small"
                style={{ 
                  backgroundColor: 'var(--bg-hover)',
                  border: '2px dashed var(--info)',
                  boxShadow: 'var(--shadow-medium)',
                  opacity: 0.8
                }}
                styles={{ body: { padding: '12px' } }}
              >
                <Text strong>{findItemById(menuItems, activeId)?.label}</Text>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <MenuItemEditModal
          item={editingItem}
          visible={!!editingItem}
          onSave={handleSaveEdit}
          onCancel={() => {
            setEditingItem(null);
            setIsAddingNew(false);
          }}
          isNew={isAddingNew}
        />
      )}
    </div>
  );
});

interface MenuItemEditModalProps {
  item: MenuItem;
  visible: boolean;
  onSave: (values: { label: string; url: string }) => void;
  onCancel: () => void;
  isNew: boolean;
}

const MenuItemEditModal: React.FC<MenuItemEditModalProps> = ({
  item,
  visible,
  onSave,
  onCancel,
  isNew
}) => {
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (visible && item) {
      form.setFieldsValue({
        label: item.label,
        url: item.url,
      });
    }
  }, [visible, item, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSave(values);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title={isNew ? 'Add Menu Item' : 'Edit Menu Item'}
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Save"
      cancelText="Cancel"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          label: item?.label || '',
          url: item?.url || '',
        }}
      >
        <Form.Item
          name="label"
          label="Menu Label"
          rules={[
            { required: true, message: 'Please enter menu label' },
            { min: 1, max: 50, message: 'Label must be between 1-50 characters' }
          ]}
        >
          <Input placeholder="Enter menu label" />
        </Form.Item>

        <Form.Item
          name="url"
          label="URL"
          rules={[
            { required: true, message: 'Please enter URL' },
            { min: 1, max: 200, message: 'URL must be between 1-200 characters' }
          ]}
        >
          <Input placeholder="Enter URL (e.g., /home, #section, https://example.com)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

DndKitMenuDragDrop.displayName = 'DndKitMenuDragDrop';

export default DndKitMenuDragDrop;
